import { routes } from "@constants/routes";
import {
  Table,
  Typography,
  UserTypeEnum,
  calculateAdRemainingDays,
  getJobPositionStatusColor,
  getJobPositionStatusLabel,
  useFetchFile,
} from "@cpj-ai/panel-npm-package";
import { AdminJobPositionNode, GetJobPositionsQuery } from "@graphql/generated";
import { Spinner, User } from "@heroui/react";
import dayjs from "dayjs";
import React, { useMemo } from "react";
import { useNavigate } from "react-router-dom";

interface JobPositionTableProps {
  jobPositions?: GetJobPositionsQuery;
  onRowClick?: (jobPositionId: string) => void;
}

const JobPositionTable: React.FC<JobPositionTableProps> = ({ jobPositions, onRowClick }) => {
  const navigate = useNavigate();

  const jobPositionsList = jobPositions?.admin?.jobPositions?.edges?.map((item) => ({
    ...item?.node,
  }));

  const handleRowClick = (row: AdminJobPositionNode) => {
    if (onRowClick) {
      onRowClick(row.id);
    } else {
      navigate(routes.jobPositions.view.replace(":id", row.id));
    }
  };

  const columns = useMemo(
    () => [
      {
        header: "Job Title",
        accessorKey: "title",
        cell: ({ row }: { row: { original: AdminJobPositionNode } }) => {
          const jobPosition = row.original as AdminJobPositionNode;
          return <Typography>{jobPosition.title || "-"}</Typography>;
        },
      },
      {
        header: "Company Name/ Nickname",
        accessorKey: "companyName",
        cell: ({ row }: { row: { original: AdminJobPositionNode } }) => {
          const jobPosition = row.original as AdminJobPositionNode;
          return <CompanyNameCell jobPosition={jobPosition} />;
        },
      },
      {
        header: "Ad Validity",
        accessorKey: "validityDate",
        cell: ({ row }: { row: { original: AdminJobPositionNode } }) => {
          const jobPosition = row.original as AdminJobPositionNode;
          const validityDate = jobPosition?.validityDate;
          const remainingValidityDays = validityDate
            ? calculateAdRemainingDays(validityDate) > 0
              ? `${calculateAdRemainingDays(validityDate)} day`
              : "Expired"
            : "Not set";
          return (
            <div className="flex items-center gap-2">
              <Typography>{validityDate ? dayjs(validityDate).format("DD/MM/YYYY") : "Not set"}</Typography>
              {validityDate && (
                <div className="px-2 py-1 rounded-lg text-white bg-default-400 text-tiny ">{remainingValidityDays}</div>
              )}
            </div>
          );
        },
      },
      {
        header: "Skills",
        accessorKey: "skills",
        cell: ({ row }: { row: { original: AdminJobPositionNode } }) => {
          const jobPosition = row.original as AdminJobPositionNode;
          const skills = jobPosition.skills?.map((skill) => skill?.title).join(", ");
          const truncatedSkills = skills?.length && skills?.length > 40 ? `${skills?.slice(0, 40)}...` : skills;
          return <Typography>{truncatedSkills || "-"}</Typography>;
        },
      },
      {
        header: "Status",
        accessorKey: "status",
        cell: ({ row }: { row: { original: AdminJobPositionNode } }) => {
          const jobPosition = row.original as AdminJobPositionNode;

          return (
            <Typography
              className={`text-xs rounded-lg text-white px-2 py-1 w-fit ${getJobPositionStatusColor(jobPosition?.status)}`}
            >
              {getJobPositionStatusLabel(jobPosition.status)}
            </Typography>
          );
        },
      },
    ],
    [],
  );

  return (
    <Table
      columns={columns}
      data={jobPositionsList as AdminJobPositionNode[]}
      onRowClick={handleRowClick}
      classNames={{
        tr: "cursor-pointer",
      }}
      bottomContentCustomComponent={<></>}
    />
  );
};

export default JobPositionTable;

const CompanyNameCell: React.FC<{ jobPosition: AdminJobPositionNode }> = ({ jobPosition }) => {
  const { data: organizationLogo, loading } = useFetchFile({
    urlPath: jobPosition.organization?.logo?.file,
    userType: UserTypeEnum.ADMIN,
  });

  return (
    <User
      key={organizationLogo}
      name={jobPosition.organization?.name}
      description={jobPosition.organization?.shortName}
      avatarProps={{
        src: organizationLogo,
        fallback: loading ? <Spinner size="sm" /> : jobPosition.organization?.name?.charAt(0),
      }}
    />
  );
};
